import {
  Button,
  HStack,
  IconButton,
  IconClose,
  Label,
  Stack,
  Table,
  Text,
} from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { SENSOR_STATUS, type SensorStatus } from '~/api/types';
import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import {
  SENSOR_STATUS_ICON,
  SENSOR_STATUS_LABEL,
  SENSOR_STATUS_VARIANT,
} from '~/components/table/patient/utils';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

const SENSOR_STATUS_DESCRIPTION: Record<SensorStatus, string> = {
  [SENSOR_STATUS.ConnectionRequest]:
    'A patient has requested to join your department. You will need to review and accept the patient before you can view the patient’s details.',
  [SENSOR_STATUS.ActiveEndingSoon]:
    'The patient’s sensor will end soon and a new sensor has not been started. Lactate data will be unavailable once the current sensor ends.',
  [SENSOR_STATUS.ActiveNoData]:
    'The patient’s sensor is not transmitting data. This may be due to a connection issue.',
  [SENSOR_STATUS.InactiveActivationPending]:
    'The patient has been invited but has not started their first sensor yet.',
  [SENSOR_STATUS.Active]: 'The patient’s sensor is working normally.',
  [SENSOR_STATUS.ActiveWarmingUp]:
    'The patient’s sensor is functioning normally. Lactate data will be available once the sensor is done warming up.',
  [SENSOR_STATUS.InactiveSensorEnded]:
    'The patient’s sensor has ended. No lactate data will be available until the patient starts a new sensor.',
  [SENSOR_STATUS.InactiveLastData]:
    'The patient has been inactive for longer than 30 days.',
};

const statuses: SensorStatus[] = Object.values(SENSOR_STATUS);

const DisplayValues: Partial<Record<SensorStatus, string>> = {
  [SENSOR_STATUS.ActiveEndingSoon]: '#h left',
  [SENSOR_STATUS.ActiveNoData]: '#h',
  [SENSOR_STATUS.ActiveWarmingUp]: '#h left',
  [SENSOR_STATUS.InactiveSensorEnded]: '# days ago',
  [SENSOR_STATUS.InactiveLastData]: '# days ago',
};

type StatusModalProps = {
  testID: string;
};

export const StatusInfoModal = ({ testID }: StatusModalProps) => {
  const { closeModal } = useModal();

  const onClose = () => {
    closeModal('StatusInfo');
  };

  const onOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal
        testID={buildTestId({
          testID,
          suffix: 'portal',
        })}
      >
        <ModalOverlay
          testID={buildTestId({
            testID,
            suffix: 'overlay',
          })}
        />
        <ModalContent
          testID={buildTestId({
            testID,
            suffix: 'content',
          })}
          key="content"
          paddingBottom="$3"
          maxWidth={1200}
          alignContent="center"
          justifyContent="space-between"
        >
          <HStack flex={1} justifyContent="space-between" marginBottom="$8">
            <Text
              type="$h3.strong"
              testID={buildTestId({ testID, suffix: 'title' })}
            >
              Sensor Status
            </Text>
            <Stack>
              <IconButton
                aria-label="Close modal"
                onPress={onClose}
                size="S"
                circle
                variant="secondary"
                // @ts-expect-error Parallax doesn't type IconButton borderWidth
                borderWidth={0}
                testID={buildTestId({ testID, suffix: ['close', 'button'] })}
              >
                <IconClose />
              </IconButton>
            </Stack>
          </HStack>
          <Stack>
            <Table aria-label="Sensor statuses">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {statuses.map((status) => (
                  <Table.Row key={status}>
                    <Table.TextCell>
                      <Label
                        variant="tertiary"
                        iconStart={SENSOR_STATUS_ICON[status]}
                        type={SENSOR_STATUS_VARIANT[status]}
                        aria-label={SENSOR_STATUS_LABEL[status]({
                          displayValue: DisplayValues[status],
                        })}
                      >
                        <Text>
                          {SENSOR_STATUS_LABEL[status]({
                            displayValue: DisplayValues[status],
                          })}
                        </Text>
                      </Label>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Text>
                        <p
                          style={{
                            width: 700,
                            padding: 0,
                          }}
                        >
                          {SENSOR_STATUS_DESCRIPTION[status]}
                        </p>
                      </Text>
                    </Table.TextCell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Stack>
          <Stack alignItems="center" marginVertical="$6">
            <Button
              variant="primary"
              label="Done"
              onPress={onClose}
              // @ts-expect-error Parallax doesn't type this prop
              minWidth={300}
              testID={buildTestId({ testID, suffix: ['button', 'done'] })}
            />
          </Stack>
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
